import { useEffect, useRef, useState } from 'react';
import { fetchEntityAttachmentsByIds } from '@/lib/entity-attachments';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';

/**
 * The first attachment of each given option (for the Card View thumbnail),
 * loaded with ONE query for the options not seen yet. Signed URLs are
 * resolved by the shared attachments implementation. `refreshKey` forces a
 * reload (e.g. after the form changed an option's files).
 */
export function useOptionThumbnails(optionIds: string[], refreshKey: unknown = 0): Record<string, GenericAttachment | undefined> {
  const [thumbs, setThumbs] = useState<Record<string, GenericAttachment | undefined>>({});
  const loaded = useRef<Set<string>>(new Set());
  const lastKey = useRef<unknown>(refreshKey);
  const idsKey = optionIds.join(',');

  useEffect(() => {
    if (lastKey.current !== refreshKey) {
      lastKey.current = refreshKey;
      loaded.current = new Set();
    }
    const missing = optionIds.filter((id) => !loaded.current.has(id));
    if (missing.length === 0) return undefined;
    let cancelled = false;
    missing.forEach((id) => loaded.current.add(id));
    fetchEntityAttachmentsByIds('selection_option', missing)
      .then((byOption) => {
        if (cancelled) return;
        setThumbs((prev) => {
          const next = { ...prev };
          for (const id of missing) {
            const list = byOption.get(id) ?? [];
            next[id] = list.find((a) => (a.file_type || '').startsWith('image/')) ?? list[0];
          }
          return next;
        });
      })
      .catch((err) => {
        console.error('[useOptionThumbnails] failed to load thumbnails', err);
        missing.forEach((id) => loaded.current.delete(id));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, refreshKey]);

  return thumbs;
}
